import { HTTPError } from 'ky';
import type { ApiHTTPError } from './client';

// Maps backend error codes to friendlier copy than the terse API description.
// Only codes that need a different phrasing are listed — everything else falls
// back to the `detail` field the API already provides.
const CODE_MESSAGES: Record<string, string> = {
  'Auth.ServiceUnavailable':
    'The authentication service is unavailable. Please try again in a moment.',
  'Auth.NotAuthorized': "You don't have permission to perform this action.",
  'Auth.InvalidCredentials': 'Incorrect email or password.',
  'Auth.RefreshTokenInvalid': 'Your session has expired. Please sign in again.',
  'Auth.RefreshTokenExpired': 'Your session has expired. Please sign in again.',
  'Auth.RefreshTokenReuse': 'Security alert: your session was invalidated. Please sign in again.',
  'Auth.EmailVerificationDeliveryFailed':
    'Could not send the verification email. Please try again.',
  'Auth.PasswordResetDeliveryFailed': 'Could not send the password reset email. Please try again.',
  'User.NotFound': 'Your account could not be found. Please contact support.',
  'Invitation.TokenInvalid': 'This invitation link is invalid or has already been used.',
  'Invitation.TokenExpired': 'This invitation link has expired. Ask your admin to resend it.',
};

const STATUS_MESSAGES: Record<number, string> = {
  400: 'The request was invalid. Please check your input and try again.',
  401: 'Your session has expired. Please sign in again.',
  403: "You don't have permission to perform this action.",
  404: 'The requested resource was not found.',
  409: 'This action conflicts with existing data.',
  422: 'The request could not be processed. Please check your input.',
  429: 'Too many requests. Please wait a moment before trying again.',
  500: 'Something went wrong on our end. Please try again.',
  502: 'The server is temporarily unavailable. Please try again.',
  503: 'The service is temporarily unavailable. Please try again.',
};

export function getApiErrorMessage(error: unknown): string {
  if (error instanceof HTTPError) {
    const apiError = error as ApiHTTPError;
    const data = apiError.data;

    if (data) {
      // Check the first error code against our friendly-message map.
      const firstCode = data.errors?.[0]?.code;
      if (firstCode && CODE_MESSAGES[firstCode]) {
        return CODE_MESSAGES[firstCode];
      }

      // Fall back to the API's detail field — already human-readable.
      if (data.detail) return data.detail;
      if (data.title) return data.title;
    }

    // Fall back to status-based message.
    const statusMessage = STATUS_MESSAGES[error.response.status];
    if (statusMessage) return statusMessage;
  }

  // Network error or unexpected JS error.
  if (error instanceof TypeError && error.message.toLowerCase().includes('fetch')) {
    return 'Cannot connect to the server. Check your internet connection.';
  }

  return 'An unexpected error occurred. Please try again.';
}

// Whether to suppress the global toast for a given error.
// 401s during the refresh flow are handled silently by the ky interceptor —
// only a 401 that reaches the cache (refresh failed) should toast.
export function shouldSuppressToast(error: unknown): boolean {
  // Zod validation errors from wire parsing are developer bugs, not user-facing.
  if (error instanceof Error && error.name === 'ZodError') return true;
  return false;
}
